// @ts-strict-ignore
import { ActionType } from 'flow/store/engine/constants';
import type {
  Action,
  Attribute,
  Expression,
  ExpressionTerm,
  OutputObjectInAction,
} from 'flow/store/engine/types';

import { getActionBorderBottom } from 'flow/editor/styles';

import { MULTISELECT_PROPS } from '../../constants';
import { getActionName, getActionTypeText, getNumericPathIndices } from '~/dashboard/views/workflows/service/actions';
import type { DropdownOption } from '../../NodeEditorDrawer/types';
import { RuleEditorDropdownMode } from '../../NodeEditorDrawer/types';
import { getProperty } from 'dot-prop';
import { getIsExpressionOperatorString } from '~/dashboard/views/workflows/store/engine/utils';
import { getErrorAttributeLabel, isErrorAttribute } from 'flow/service/services';


/* Generates action or mode-specific styles in a style object to feed into the react-select style functions
 */
export function getDropdownTextStyles(action: Action, inlineSuggestion: boolean) {
  switch (action?.type) {
    case ActionType.SERVICE: {
      return {
        fontWeight: 'bold',
        color: 'var(--green-green-550, #3A795F)',
      };
    }
    case ActionType.OUTPUT:
    case ActionType.INPUT:
    case ActionType.MATRIX_MODEL: {
      return {
        fontWeight: 'bold',
        color: 'var(--cl-juicyBlue-500)',
      };
    }
    default:
      break;
  }
  return {
    color: inlineSuggestion ? 'var(--cl-yellow-400)' : 'var(--cl-text-default)',
    fontWeight: 'bold',
  };
}


export function getMultiSelectProps(action: Action, mode, inlineSuggestion = false) {
  return {
    ...MULTISELECT_PROPS,
    customStyleFunctions: {
      ...MULTISELECT_PROPS.customStyleFunctions,
      container: styles => ({
        ...styles,
        margin: '0',
      }),
      singleValue: styles => ({
        ...styles,
        borderTop: '2px solid transparent',
        borderBottom: getActionBorderBottom(action),
        fontFamily: 'var(--cl-font-family-mono)',
        fontSize: 'var(--cl-font-size-2)',
        ...(getDropdownTextStyles(action, inlineSuggestion)),
      }),
      valueContainer: styles => ({
        ...styles,
        padding: mode === 'caretExpression' ? '0 1px' : '0 3px',
        caretColor: 'var(--cl-yellow-500)',
      }),
    },
  };
}

const isValidAvailableAction = (
  action: Action,
  publicId: string,
  outgoers: Set<string>,
) => (
  publicId !== action.publicId
  && action.type !== ActionType.OUTCOME
  && !outgoers.has(action.publicId)
);

export const getAvailableActionsAsDropdownOptions = (
  actions: Record<string, Action>,
  publicId: string,
  outgoers: Set<string>,
) => Object.entries(actions)
  .filter(([, action]) => isValidAvailableAction(action, publicId, outgoers))
  .map(([id, action]) => {
    const actionName = getActionName(action);
    return {
      label: `${getActionTypeText(action)}: ${actionName}`,
      value: id,
      meta: {
        category: 'action',
        shortenedLabel: `${actionName}${action.type === 'service' ? ': ' : ''}`,
      },
    } as DropdownOption;
  })
  .sort((a, b) => a.label.localeCompare(b.label));

export const getAvailableAttributesAsDropdownOptions = (
  allowedAttributes: { [attribute_id: number]: Attribute },
) => Object.entries(allowedAttributes)
  .filter(([, attribute]) => (!attribute.archivedAt))
  .sort((a, b) => a[1].display_name.localeCompare(b[1].display_name))
  .map(([id, attribute]) => ({
    label: isErrorAttribute(attribute)
      ? getErrorAttributeLabel(attribute)
      : attribute.display_name,
    value: id,
    meta: {
      category: 'attribute',
      isMLA: attribute.is_multilevel,
      showAttributeWidget: isErrorAttribute(attribute),
      attribute,
    },
  } as DropdownOption));

export const getPreviousItemInExpressionScope = (
  {
    expressions, expressionPath, mode, output,
  }: {
    expressions?: Expression;
    expressionPath?: string;
    mode: RuleEditorDropdownMode;
    output: OutputObjectInAction;
  },
) => {
  if (mode === RuleEditorDropdownMode.CARET_EXPRESSION) {
    // morphing caret is in first position, has no previous item
    if (expressionPath === '') return undefined;

    if (expressionPath) {
      // dropdown is in a position where it's empty (not created)
      const indices = getNumericPathIndices(expressionPath);

      // get previous item to generate relevant options for current item
      if (indices.length === 1) return expressions[Number(indices[0])];

      const pathWithoutLastIndex = indices.slice(0, -1)
        .reduce((result, indexValue) => `${result}[${indexValue}]`, '');
      const parentExpressionArrayOfTheExpressionOnTheLastIndex = getProperty(
        output.expression, pathWithoutLastIndex) as ExpressionTerm[];

      return parentExpressionArrayOfTheExpressionOnTheLastIndex[Number(indices.at(-1))];
    }
  }

  return expressions?.at(-1) || undefined;
};

export const canAddAvailableActionsToDropdown = (
  expressions: Expression,
  previousItemInExpressionsScope: ExpressionTerm | ExpressionTerm[],
) => {
  const isPreviousExpressionAnOperator = (
    typeof previousItemInExpressionsScope === 'string'
    && getIsExpressionOperatorString(previousItemInExpressionsScope)
  );

  const isPreviousItemAPopulatedGroup = (
    Array.isArray(previousItemInExpressionsScope)
    && !previousItemInExpressionsScope.length
  );

  const isFirstItemInExpression = previousItemInExpressionsScope === undefined;
  const isExpressionEmpty = expressions.length <= 0;

  return (
    isPreviousExpressionAnOperator
    || isPreviousItemAPopulatedGroup
    || isFirstItemInExpression
    || isExpressionEmpty
  );
};
